enum GaEvents {
    FormStart = "form_start",
    FormInteraction = "form_interaction",
    PageView = "page",
    FormSuccess = 'form_success',
}

export enum FormName {
    BusinessRegistration = "business_registration",
    Contact = "contact",
}

type FormInteractionType = 'submit' | 'view' | 'return' | 'error' | 'skip';

interface FormStartEventData {
    event: GaEvents;
    form_id: string;
    form_name: FormName;
    eventCallback?: () => void;
    ab_test_variant?: string;
    company?: {
        company_id: number,
    };
}

class GTMFormEvent {
    event: GaEvents;
    form_id?: string;
    form_version_id?: string;
    form_name: FormName;
    form_phase_name?: string;
    form_phase?: string;
    form_total_phases?: string;
    form_step_name: string;
    form_step: string;
    form_total_steps: string;
    form_value?: { [key: string]: any };
    form_interaction: FormInteractionType;
    ab_test_variant?: string;
    company?: {
        company_id: number,
    };

    constructor(event: GaEvents, form_name: FormName) {
        this.event = event;
        this.form_name = form_name;
    }
}

export interface TrackingData {
    audienceType: string;
    pageType: string;
    pageTemplate: string;
    locale: string;
    businessUnit: string;
    brandId: number;
    brandName: number;
    categoryId?: number | null;
    productName?: string | null;
}

export class B2bGATracker {
    public static allowDebugging = false;

    public static sendFormStartEvent(form_name: FormName, company_id?: number, abTest?: string): Promise<void> {
        return new Promise((resolve) => {
            const timeout = setTimeout(() => {
                console.warn("GTM callback did not fire in time");
                resolve();
            }, 500);// fallback in case GTM doesn't fire callback or isn't present
            const formElement = document.querySelector('[data-b2b-form-authcode]') as HTMLElement;

            const formStartEventData: FormStartEventData = {
                event: GaEvents.FormStart,
                form_id: form_name === FormName.BusinessRegistration
                    ? formElement.getAttribute('data-b2b-form-authcode')
                    : undefined,
                form_name: form_name,
                eventCallback: () => { // callback will not be fired if it's not set up in GTM!!! fallback above will handle that case
                    clearTimeout(timeout);
                    resolve();
                },
            };

            if (company_id !== undefined) {
                formStartEventData.company = {company_id};
            }

            if (abTest) {
                formStartEventData.ab_test_variant = abTest;
            }

            (window as any).dataLayer = (window as any).dataLayer || [];
            (window as any).dataLayer.push(formStartEventData);
            this.debugEvent(formStartEventData);
        });
    }

    public static sendPageViewEvent(
        trackingData: TrackingData,
    ): void {

        let pageViewEventData = {
            event: GaEvents.PageView,
            page_location: window.location.href,
            audience_type: trackingData.audienceType,
            page_type: trackingData.pageType,
            page_template: trackingData.pageTemplate,
            locale: trackingData.locale,
            brand_id: trackingData.brandId,
            brand_name: trackingData.brandName,
            business_category_id: trackingData.categoryId,
            business_category_name: trackingData.productName,
            business_unit: trackingData.businessUnit,
        };

        const abVersion = document.body.getAttribute('data-ab');
        if (abVersion) {
            pageViewEventData.ab_test_variant = abVersion;
        }

        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).dataLayer.push(pageViewEventData);
        this.debugEvent(pageViewEventData);
    }

    public static sendFormInteractionEvent(
        form_name: FormName,
        form_interaction: FormInteractionType,
        form_step: string,
        form_total_steps: string,
        form_step_name: string,
        abTest?: string,
        form_value?: { [key: string]: any }
    ): void {
        const formEvent = new GTMFormEvent(GaEvents.FormInteraction, form_name);
        formEvent.form_interaction = form_interaction;
        formEvent.form_step = form_step;
        formEvent.form_total_steps = form_total_steps;
        formEvent.form_step_name = form_step_name;

        if (abTest) {
            formEvent.ab_test_variant = abTest;
        }

        if (form_value !== undefined && form_interaction === 'submit') {
            formEvent.form_value = form_value;
        }

        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).dataLayer.push(formEvent);
        this.debugEvent(formEvent);
    }

    public static listenToB2BRegistrationCTAClick(): void {
        const registrationLinks = document.querySelectorAll('a[href*="/companies/registration"]');

        registrationLinks.forEach(link => {
            link.addEventListener('click', () => {
                B2bGATracker.sendFormStartEvent(FormName.BusinessRegistration)
                    .catch(error => console.error('Error handling registration link click:', error));
            });
        });
    }

    private static debugEvent(event: Record<string, any>): void {
        if (!this.allowDebugging || !window.localStorage.getItem('ga-debug')) {
            return;
        }

        // eslint-disable-next-line no-console
        console.info('GA event pushed:', event);
    }
}

if (window.location.hash === '#enable-ga-debug') {
    window.localStorage.setItem('ga-debug', '1');
    // eslint-disable-next-line no-console
    console.info('GA debug enabled');
}

(<any>window).B2bGATracker = B2bGATracker;
window.addEventListener('load', () => {
    B2bGATracker.listenToB2BRegistrationCTAClick();
});
